import React, { useEffect, useState } from "react";
import { Button, Card, Col, Row, Spinner, Alert, Table, Badge, ProgressBar, Tabs, Tab, Form, Dropdown } from "react-bootstrap";
import { Link } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatNumber, formatDate } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import './VentesJour.css';

export default function VentesJour() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [activeTab, setActiveTab] = useState('factures');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchVentesJour();
    
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchVentesJour, 60000);
    }
    return () => clearInterval(interval);
  }, [selectedDate, autoRefresh]);

  const fetchVentesJour = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_BASE_URL}/ventes_jour.php?date=${selectedDate}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (!response.ok) throw new Error("Erreur de chargement des données");
      
      const result = await response.json();
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
  };

  const handleRefresh = () => {
    fetchVentesJour();
  };

  const getStatusBadge = (statut) => {
    const badges = {
      'emise': { bg: '#f59e0b', text: 'Émise', icon: 'ri-time-line' },
      'payee': { bg: '#10b981', text: 'Payée', icon: 'ri-checkbox-circle-line' },
      'payee_partielle': { bg: '#3b82f6', text: 'Partielle', icon: 'ri-indeterminate-circle-line' },
      'impayee': { bg: '#ef4444', text: 'Impayée', icon: 'ri-close-circle-line' },
      'annulee': { bg: '#6b7280', text: 'Annulée', icon: 'ri-forbid-line' }
    };
    const badge = badges[statut] || { bg: '#6b7280', text: statut, icon: 'ri-question-line' };
    return (
      <span className="status-badge" style={{ background: `${badge.bg}15`, color: badge.bg }}>
        <i className={`${badge.icon} me-1 fs-10`}></i>
        {badge.text}
      </span>
    );
  };

  const getModePaiementLabel = (mode) => {
    const labels = {
      'especes': '💰 Espèces',
      'cheque': '📝 Chèque',
      'virement': '🏦 Virement',
      'carte_bancaire': '💳 Carte bancaire',
      'traite': '📄 Traite'
    };
    return labels[mode] || mode;
  };

  const seriesVentesHeure = [{
    name: 'Montant des ventes',
    data: data?.ventes_par_heure?.map(item => item.montant_total) || []
  }];

  const optionsVentesHeure = {
    chart: {
      type: 'area',
      height: 350,
      toolbar: { show: false },
      zoom: { enabled: false },
      fontFamily: 'Inter, system-ui, sans-serif'
    },
    colors: ['#4361ee'],
    fill: {
      type: 'gradient',
      gradient: { shadeIntensity: 1, opacityFrom: 0.7, opacityTo: 0.1 }
    },
    stroke: { curve: 'smooth', width: 2 },
    grid: { borderColor: '#e9ecef', strokeDashArray: 5 },
    xaxis: {
      categories: data?.ventes_par_heure?.map(item => `${item.heure}h`) || [],
      labels: { style: { fontSize: '11px', fontWeight: 500 } }
    },
    yaxis: {
      labels: { formatter: (value) => formatCurrency(value), style: { fontSize: '11px' } }
    },
    tooltip: { y: { formatter: (value) => formatCurrency(value) } }
  };

  const seriesRepartitionPaiements = data?.moyens_paiement?.map(item => item.total) || [];

  const donutOptions = {
    chart: { 
      type: 'donut', 
      height: 280, 
      fontFamily: 'Inter, system-ui, sans-serif',
      toolbar: { show: false }
    },
    labels: data?.moyens_paiement?.map(item => getModePaiementLabel(item.mode_reglement)) || [],
    colors: ['#4361ee', '#06b6d4', '#10b981', '#f59e0b', '#ef4444'],
    legend: { 
      position: 'bottom', 
      fontSize: '11px', 
      offsetY: 5,
      markers: { radius: 8 }
    },
    tooltip: { 
      y: { 
        formatter: (value) => formatCurrency(value)
      } 
    },
    plotOptions: {
      pie: {
        donut: {
          size: '65%',
          labels: {
            show: true,
            total: {
              show: true,
              label: 'Total',
              fontSize: '14px',
              fontWeight: 600,
              color: '#1a3c5e',
              formatter: () => formatCurrency(data?.moyens_paiement?.reduce((acc, item) => acc + item.total, 0) || 0)
            }
          }
        }
      }
    },
    dataLabels: { 
      style: { fontSize: '11px', fontWeight: 500 } 
    },
    stroke: { show: false }
  };

  if (loading && !data) {
    return (
      <>
        <Header />
        <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
            <div className="text-center">
              <div className="loading-animation mb-4">
                <div className="loading-ring">
                  <div className="loading-ring-inner"></div>
                </div>
                <i className="ri-shopping-bag-3-line loading-icon"></i>
              </div>
              <h5 className="fw-bold text-primary mb-2">Chargement des ventes</h5>
              <p className="text-muted small mb-0">Récupération des données en cours...</p>
              <div className="mx-auto mt-4" style={{ width: '200px' }}>
                <div className="progress" style={{ height: '3px', borderRadius: '3px', background: '#e9ecef' }}>
                  <div className="progress-bar progress-bar-striped progress-bar-animated" style={{ width: '100%', background: 'linear-gradient(90deg, #4361ee, #10b981, #f59e0b)' }} />
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
        <style>{`
          .loading-animation { position: relative; width: 80px; height: 80px; margin: 0 auto; }
          .loading-ring { position: absolute; top: 0; left: 0; width: 100%; height: 100%; border-radius: 50%; background: linear-gradient(135deg, #4361ee, #10b981); animation: pulse-ring 1.5s ease-in-out infinite; }
          .loading-ring-inner { position: absolute; top: 5px; left: 5px; right: 5px; bottom: 5px; border-radius: 50%; background: white; }
          .loading-icon { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 28px; color: #4361ee; z-index: 2; animation: pulse-icon 1.5s ease-in-out infinite; }
          @keyframes pulse-ring { 0% { transform: scale(0.9); opacity: 0.7; } 50% { transform: scale(1.1); opacity: 0.3; } 100% { transform: scale(0.9); opacity: 0.7; } }
          @keyframes pulse-icon { 0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 1; } 50% { transform: translate(-50%, -50%) scale(1.1); opacity: 0.8; } }
        `}</style>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <div className="main main-app p-3 p-lg-4">
          <Alert variant="danger" className="border-0 shadow-sm rounded-0">
            <div className="d-flex align-items-center">
              <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
              <div>
                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                <p className="mb-0 small">{error}</p>
                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={handleRefresh}>
                  <i className="ri-refresh-line me-1"></i>Réessayer
                </Button>
              </div>
            </div>
          </Alert>
        </div>
        <Footer />
      </>
    );
  }

  const today = new Date().toISOString().split('T')[0];
  const isToday = selectedDate === today;

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
        
        {/* En-tête moderne */}
        <div className="d-md-flex align-items-center justify-content-between mb-4">
          <div>
            <div className="d-flex align-items-center gap-2 mb-2">
              <div className="p-2 bg-primary bg-opacity-10 rounded-0">
                <i className="ri-shopping-bag-3-line fs-20 text-primary"></i>
              </div>
              <div>
                <h4 className="main-title mb-0 fw-bold">
                  {isToday ? "Ventes d'aujourd'hui" : `Ventes du ${formatDate(selectedDate)}`}
                </h4>
                <p className="text-muted small mb-0">Suivi en temps réel des transactions</p>
              </div>
            </div>
          </div>
          <div className="d-flex gap-2 mt-3 mt-md-0">
            <div className="position-relative">
              <i className="ri-calendar-line position-absolute top-50 start-0 translate-middle-y ms-3 text-muted fs-14"></i>
              <Form.Control
                type="date"
                value={selectedDate}
                onChange={handleDateChange}
                className="ps-5 rounded-pill"
                style={{ width: '160px' }}
                max={today}
              />
            </div>
            <div className="form-check form-switch d-flex align-items-center ms-2">
              <input
                className="form-check-input me-2"
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                id="autoRefresh"
              />
              <label className="form-check-label small text-muted" htmlFor="autoRefresh">
                Auto-refresh
              </label>
            </div>
            <Button 
              variant="outline-primary" 
              size="sm"
              onClick={handleRefresh}
              disabled={loading}
              className="d-flex align-items-center gap-1 rounded-pill px-3"
            >
              <i className={`ri-refresh-line ${loading ? 'fa-spin' : ''}`}></i>
              Actualiser
            </Button>
          </div>
        </div>

        {/* KPI Cards modernes */}
        <Row className="g-3 mb-4">
          <Col sm="6" xl="3">
            <div className="kpi-card kpi-primary">
              <div className="kpi-icon">
                <i className="ri-money-euro-circle-line fs-24"></i>
              </div>
              <div className="kpi-content">
                <span className="kpi-label">Chiffre d'affaires</span>
                <h3 className="kpi-value">{formatCurrency(data?.resume?.total_ttc || 0)}</h3>
                <span className="kpi-unit">{Math.abs(data?.comparaison?.evolution || 0)}% vs hier</span>
              </div>
            </div>
          </Col>

          <Col sm="6" xl="3">
            <div className="kpi-card kpi-success">
              <div className="kpi-icon">
                <i className="ri-file-list-3-line fs-24"></i>
              </div>
              <div className="kpi-content">
                <span className="kpi-label">Factures</span>
                <h3 className="kpi-value">{data?.resume?.nb_factures || 0}</h3>
                <span className="kpi-unit">{Math.abs(data?.comparaison?.evolution_factures || 0)}% vs hier</span>
              </div>
            </div>
          </Col>

          <Col sm="6" xl="3">
            <div className="kpi-card kpi-warning">
              <div className="kpi-icon">
                <i className="ri-group-line fs-24"></i>
              </div>
              <div className="kpi-content">
                <span className="kpi-label">Clients servis</span>
                <h3 className="kpi-value">{data?.resume?.nb_clients || 0}</h3>
                <span className="kpi-unit">Panier moyen: {formatCurrency(data?.resume?.nb_factures > 0 ? data?.resume?.total_ttc / data?.resume?.nb_factures : 0)}</span>
              </div>
            </div>
          </Col>

          <Col sm="6" xl="3">
            <div className="kpi-card kpi-info">
              <div className="kpi-icon">
                <i className="ri-stack-line fs-24"></i>
              </div>
              <div className="kpi-content">
                <span className="kpi-label">Produits vendus</span>
                <h3 className="kpi-value">{data?.resume?.quantite_totale || 0}</h3>
                <span className="kpi-unit">{data?.resume?.nb_produits || 0} références</span>
              </div>
            </div>
          </Col>
        </Row>

        {/* Graphiques */}
        <Row className="g-3 mb-4">
          <Col xl="8">
            <Card className="border-0 shadow-sm rounded-0">
              <Card.Header className="bg-transparent border-0 pt-3 px-4 d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-2">
                  <i className="ri-bar-chart-line text-primary"></i>
                  <h6 className="mb-0 fw-semibold">Ventes par heure</h6>
                </div>
                <Badge bg="light" text="dark" className="rounded-pill px-3 py-1">
                  Total: {formatCurrency(data?.ventes_par_heure?.reduce((acc, item) => acc + item.montant_total, 0) || 0)}
                </Badge>
              </Card.Header>
              <Card.Body className="px-4 pb-4">
                <ReactApexChart series={seriesVentesHeure} options={optionsVentesHeure} type="area" height={350} />
              </Card.Body>
            </Card>
          </Col>

          <Col xl="4">
            <Card className="border-0 shadow-sm rounded-0 h-100">
              <Card.Header className="bg-transparent border-0 pt-3 px-4">
                <div className="d-flex align-items-center gap-2">
                  <i className="ri-pie-chart-2-line text-success"></i>
                  <h6 className="mb-0 fw-semibold">Répartition des paiements</h6>
                </div>
              </Card.Header>
              <Card.Body className="px-4 pb-4">
                {data?.moyens_paiement?.length > 0 ? (
                  <>
                    <ReactApexChart series={seriesRepartitionPaiements} options={donutOptions} type="donut" height={260} />
                    <div className="mt-3">
                      {data?.moyens_paiement?.map((paiement, index) => {
                        const colors = ['#4361ee', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
                        return (
                          <div key={index} className="d-flex justify-content-between align-items-center py-2 border-bottom">
                            <div className="d-flex align-items-center gap-2">
                              <div className="p-1 rounded bg-primary bg-opacity-10">
                                <i className={`ri-bank-card-line fs-14 text-primary`}></i>
                              </div>
                              <span className="small fw-semibold">{getModePaiementLabel(paiement.mode_reglement)}</span>
                            </div>
                            <div>
                              <span className="fw-bold me-2">{formatCurrency(paiement.total)}</span>
                              <Badge bg="light" text="dark" className="rounded-pill">{paiement.nb_paiements}</Badge>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-5">
                    <i className="ri-pie-chart-2-line fs-48 text-muted mb-3 d-block"></i>
                    <p className="text-muted mb-0">Aucun paiement enregistré</p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Onglets */}
        <Card className="border-0 shadow-sm rounded-0">
          <Card.Header className="bg-transparent border-0 pt-3 px-4">
            <div className="d-flex gap-3 border-bottom pb-2">
              <Button 
                variant="link" 
                className={`p-0 text-decoration-none d-flex align-items-center gap-2 ${activeTab === 'factures' ? 'text-primary fw-bold' : 'text-muted'}`}
                onClick={() => setActiveTab('factures')}
              >
                <i className="ri-file-list-3-line"></i>
                <span>Factures</span>
                <Badge bg={activeTab === 'factures' ? 'primary' : 'light'} className="rounded-pill">{data?.factures?.length || 0}</Badge>
              </Button>
              <Button 
                variant="link" 
                className={`p-0 text-decoration-none d-flex align-items-center gap-2 ${activeTab === 'produits' ? 'text-primary fw-bold' : 'text-muted'}`}
                onClick={() => setActiveTab('produits')}
              >
                <i className="ri-suitcase-2-line"></i>
                <span>Produits</span>
                <Badge bg={activeTab === 'produits' ? 'primary' : 'light'} className="rounded-pill">{data?.produits?.length || 0}</Badge>
              </Button>
              <Button 
                variant="link" 
                className={`p-0 text-decoration-none d-flex align-items-center gap-2 ${activeTab === 'paiements' ? 'text-primary fw-bold' : 'text-muted'}`}
                onClick={() => setActiveTab('paiements')}
              >
                <i className="ri-bank-card-line"></i>
                <span>Paiements</span>
                <Badge bg={activeTab === 'paiements' ? 'primary' : 'light'} className="rounded-pill">{data?.paiements?.length || 0}</Badge>
              </Button>
              <Button 
                variant="link" 
                className={`p-0 text-decoration-none d-flex align-items-center gap-2 ${activeTab === 'vendeurs' ? 'text-primary fw-bold' : 'text-muted'}`}
                onClick={() => setActiveTab('vendeurs')}
              >
                <i className="ri-user-star-line"></i>
                <span>Performance vendeurs</span>
              </Button>
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            
            {/* Onglet Factures */}
            {activeTab === 'factures' && (
              <div className="table-responsive">
                <Table hover className="align-middle mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th className="ps-4">N° Facture</th>
                      <th>Heure</th>
                      <th>Client</th>
                      <th className="text-end">Montant HT</th>
                      <th className="text-end">TVA</th>
                      <th className="text-end">TTC</th>
                      <th className="text-end">Réglé</th>
                      <th className="text-end">Reste</th>
                      <th>Statut</th>
                      <th>Vendeur</th>
                      <th className="pe-4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.factures?.map((facture) => (
                      <tr key={facture.id_facture}>
                        <td className="ps-4 fw-semibold">
                          <Link to={`/ventes/factures/${facture.id_facture}`} className="text-primary text-decoration-none">
                            {facture.numero_facture}
                          </Link>
                        </td>
                        <td className="text-muted">{new Date(facture.date_facture).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</td>
                        <td>
                          <div className="fw-semibold">{facture.raison_sociale}</div>
                          <small className="text-muted">{facture.code_client}</small>
                        </td>
                        <td className="text-end">{formatCurrency(facture.montant_ht)}</td>
                        <td className="text-end text-warning">{formatCurrency(facture.montant_tva)}</td>
                        <td className="text-end fw-bold">{formatCurrency(facture.montant_ttc)}</td>
                        <td className="text-end text-success">{formatCurrency(facture.montant_regle)}</td>
                        <td className="text-end text-danger">{formatCurrency(facture.montant_ttc - facture.montant_regle)}</td>
                        <td>{getStatusBadge(facture.statut)}</td>
                        <td><small>{facture.prenom_utilisateur} {facture.nom_utilisateur}</small></td>
                        <td className="pe-4">
                          <button className="action-icon" title="Imprimer">
                            <i className="ri-printer-line"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-light fw-bold">
                    <tr>
                      <td colSpan="5" className="text-end ps-4">TOTAUX:</td>
                      <td className="text-end">{formatCurrency(data?.resume?.total_ttc || 0)}</td>
                      <td className="text-end">{formatCurrency(data?.resume?.total_regle || 0)}</td>
                      <td className="text-end pe-4">{formatCurrency(data?.resume?.reste_a_payer || 0)}</td>
                      <td colSpan="3"></td>
                    </tr>
                  </tfoot>
                </Table>
              </div>
            )}

            {/* Onglet Produits */}
            {activeTab === 'produits' && (
              <div className="table-responsive">
                <Table hover className="align-middle mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th className="ps-4">Référence</th>
                      <th>Produit</th>
                      <th>Catégorie</th>
                      <th className="text-end">Quantité</th>
                      <th className="text-end">Prix unitaire</th>
                      <th className="text-end">Total HT</th>
                      <th className="text-end pe-4">% des ventes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.produits?.map((produit) => {
                      const pourcentage = (produit.total_ligne_ht / (data?.resume?.total_ht || 1)) * 100;
                      return (
                        <tr key={produit.id_produit}>
                          <td className="ps-4"><span className="reference-badge">{produit.reference}</span></td>
                          <td className="fw-semibold">{produit.designation}</td>
                          <td className="text-muted">{produit.nom_categorie || '-'}</td>
                          <td className="text-end fw-bold">{formatNumber(produit.quantite_vendue)}</td>
                          <td className="text-end">{formatCurrency(produit.prix_unitaire_ht)}</td>
                          <td className="text-end text-primary fw-semibold">{formatCurrency(produit.total_ligne_ht)}</td>
                          <td className="text-end pe-4">
                            <div className="d-flex align-items-center justify-content-end gap-2">
                              <div className="progress-bar-custom" style={{ width: '80px', height: '5px' }}>
                                <div className="progress-fill-custom" style={{ width: `${pourcentage}%`, background: '#10b981' }}></div>
                              </div>
                              <small className="text-muted">{pourcentage.toFixed(1)}%</small>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}

            {/* Onglet Paiements */}
            {activeTab === 'paiements' && (
              <div className="table-responsive">
                <Table hover className="align-middle mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th className="ps-4">N° Opération</th>
                      <th>Heure</th>
                      <th>Client</th>
                      <th>Facture</th>
                      <th>Mode</th>
                      <th>Référence</th>
                      <th className="text-end">Montant</th>
                      <th className="pe-4">Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.paiements?.map((paiement) => (
                      <tr key={paiement.id_reglement}>
                        <td className="ps-4 fw-semibold font-monospace">{paiement.numero_reglement}</td>
                        <td className="text-muted">{new Date(paiement.date_reglement).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</td>
                        <td className="fw-semibold">{paiement.client}</td>
                        <td>{paiement.numero_facture}</td>
                        <td>
                          <span className="payment-mode-badge">
                            {getModePaiementLabel(paiement.mode_reglement)}
                          </span>
                        </td>
                        <td className="text-muted small">{paiement.reference || '-'}</td>
                        <td className="text-end fw-bold text-success">{formatCurrency(paiement.montant)}</td>
                        <td className="pe-4">
                          <span className={`status-dot status-${paiement.statut === 'valide' ? 'success' : 'warning'}`}>
                            {paiement.statut === 'valide' ? 'Validé' : 'En attente'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-light fw-bold">
                    <tr>
                      <td colSpan="6" className="text-end ps-4">TOTAL:</td>
                      <td className="text-end text-success">{formatCurrency(data?.paiements?.reduce((acc, p) => acc + p.montant, 0) || 0)}</td>
                      <td className="pe-4"></td>
                    </tr>
                  </tfoot>
                </Table>
              </div>
            )}

            {/* Onglet Vendeurs */}
            {activeTab === 'vendeurs' && (
              <Row className="g-3 p-4">
                {data?.top_vendeurs?.map((vendeur, index) => {
                  const pourcentage = (vendeur.total_ventes / (data?.resume?.total_ttc || 1)) * 100;
                  return (
                    <Col md="4" key={vendeur.id_utilisateur}>
                      <Card className="border-0 shadow-sm rounded-0 h-100 hover-lift">
                        <Card.Body className="p-3">
                          <div className="d-flex align-items-center mb-3">
                            <div className="rounded-circle me-3 d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', background: `linear-gradient(135deg, ${['#4361ee', '#10b981', '#f59e0b'][index % 3]}, ${['#1a3c5e', '#0d9488', '#d97706'][index % 3]})` }}>
                              <span className="text-white fw-bold fs-16">#{index + 1}</span>
                            </div>
                            <div>
                              <h6 className="mb-0 fw-semibold">{vendeur.prenom_utilisateur} {vendeur.nom_utilisateur}</h6>
                              <small className="text-muted">{vendeur.nb_ventes} ventes</small>
                            </div>
                          </div>
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <span className="text-muted small">CA réalisé</span>
                            <span className="fw-bold text-primary">{formatCurrency(vendeur.total_ventes)}</span>
                          </div>
                          <div className="progress-bar-custom" style={{ height: '6px' }}>
                            <div className="progress-fill-custom" style={{ width: `${pourcentage}%`, background: '#10b981' }}></div>
                          </div>
                          <small className="text-muted d-block text-end mt-1">
                            {pourcentage.toFixed(1)}% du total
                          </small>
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            )}
          </Card.Body>
        </Card>

        <Footer />
      </div>

      <style>{`
        /* KPI Cards */
        .kpi-card {
          background: white;
          border-radius: 0px;
          padding: 1rem;
          display: flex;
          align-items: center;
          gap: 1rem;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
          transition: all 0.2s ease;
          height: 100%;
          border-left: 2px solid;
        }
        .kpi-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0,0,0,0.08);
        }
        .kpi-primary { border-left-color: #4361ee; }
        .kpi-success { border-left-color: #10b981; }
        .kpi-warning { border-left-color: #f59e0b; }
        .kpi-info { border-left-color: #0dcaf0; }
        
        .kpi-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .kpi-primary .kpi-icon { background: #e8f0fe; color: #4361ee; }
        .kpi-success .kpi-icon { background: #ecfdf5; color: #10b981; }
        .kpi-warning .kpi-icon { background: #fffbeb; color: #f59e0b; }
        .kpi-info .kpi-icon { background: #e2f3f5; color: #0dcaf0; }
        
        .kpi-content {
          flex: 1;
        }
        .kpi-label {
          font-size: 0.7rem;
          text-transform: uppercase;
          color: #6c757d;
          font-weight: 600;
          letter-spacing: 0.5px;
        }
        .kpi-value {
          font-size: 1.3rem;
          font-weight: 700;
          margin: 0.25rem 0;
          color: #1a3c5e;
        }
        .kpi-unit {
          font-size: 0.7rem;
          color: #6c757d;
        }
        
        /* Status Badge */
        .status-badge {
          display: inline-flex;
          align-items: center;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 500;
        }
        
        /* Reference Badge */
        .reference-badge {
          background: #f8f9fa;
          padding: 0.2rem 0.6rem;
          border-radius: 6px;
          font-size: 0.7rem;
          font-family: monospace;
        }
        
        /* Progress Bar Custom */
        .progress-bar-custom {
          background: #e9ecef;
          border-radius: 10px;
          overflow: hidden;
        }
        .progress-fill-custom {
          height: 100%;
          border-radius: 10px;
          transition: width 0.3s ease;
        }
        
        /* Action Icon */
        .action-icon {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: #f8f9fa;
          border: 1px solid #e9ecef;
          color: #6c757d;
          transition: all 0.2s;
          cursor: pointer;
        }
        .action-icon:hover {
          background: #4361ee;
          border-color: #4361ee;
          color: white;
        }
        
        /* Payment Mode Badge */
        .payment-mode-badge {
          display: inline-flex;
          align-items: center;
          padding: 0.2rem 0.6rem;
          background: #f8f9fa;
          border-radius: 20px;
          font-size: 0.75rem;
        }
        
        .status-dot {
          display: inline-block;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.7rem;
          font-weight: 500;
        }
        .status-success { background: #ecfdf5; color: #10b981; }
        .status-warning { background: #fffbeb; color: #f59e0b; }
        
        .hover-lift {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .hover-lift:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 20px rgba(0,0,0,0.08) !important;
        }
        
        .fs-10 { font-size: 0.625rem; }
        .fs-14 { font-size: 0.875rem; }
        .fs-16 { font-size: 1rem; }
        .font-monospace { font-family: 'Courier New', monospace; }
      `}</style>
    </>
  );
}